// VideoSummary.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getRelativeTimeShort } from 'utilities/DateTime';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './VideoSummary.css';

/**
 * VideoSummary
 *
 * Displays a single video summary in a list view.
 * 
 * @param {object} node The video node.
 * @param {Function} onClick The click node handler.
 * @param {boolean} focused Whether the summary is focused.
 * 
 * @returns {ReactNode} VideoSummary component.
 */
const VideoSummary = ({ node, onClick, focused }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- UTILITIES ----- //

  // Format duration from seconds to mm:ss

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format view count (e.g., 49000 -> "49K views")
  const formatViewCount = (count) => {
    if (!count || count === 0) return '0 views';

    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1).replace('.0', '')}M views`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace('.0', '')}K views`;
    } else {
      return `${count} view${count === 1 ? '' : 's'}`;
    }
  };

  // ----- MEMOS ----- //

  const durationText = useMemo(() => formatDuration(node?.duration), [node?.duration]);
  const viewCountText = useMemo(() => formatViewCount(node?.viewCount), [node?.viewCount]);

  // ----- STATE ----- //

  const [coverSrc, setCoverSrc] = useState(null);
  const [hovered, setHovered] = useState(false);

  // ----- EFFECTS ----- //

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = await resolveImageUrl({ nodeKey: node?.key || null, url: node?.coverUrl || null });
      if (cancelled) return;
      setCoverSrc(url || null);
    })();

    return () => {
      cancelled = true;
    };
  }, [node]);

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div
      className={`video-summary-container${focused ? ' focused' : ''}`}
      style={{
        backgroundColor: focused
          ? theme.backgroundHover
          : hovered
            ? theme.backgroundMuted
            : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* LEFT */}
      <div className="video-summary-left">

        {node?.coverUrl && (
          <div className="video-summary-cover">

            {/* COVER IMAGE */}
            <img
              className="video-summary-cover-image"
              src={coverSrc || node?.coverUrl}
              alt={node?.title}
            />

            {/* DURATION OVERLAY */}
            {durationText && (
              <div className="video-summary-duration-overlay">
                {durationText}
              </div>
            )}
          </div>
        )}

      </div>

      {/* RIGHT */}
      <div className="video-summary-right">

        {/* TITLE */}
        <div className="video-summary-title">
          {node?.title}
        </div>

        {/* CREATOR */}
        <div
          className="video-summary-creator"
        >
          {node?.user?.name}
        </div>

        {/* FOOTER */}
        <div
          className="video-summary-footer"
        >

          {viewCountText && (
            <>

              {/* VIEW COUNT */}
              <div className="video-summary-view-count">
                {viewCountText}
              </div>
              •
            </>
          )}

          {/* ELAPSED */}
          <div className="video-summary-elapsed">
            {getRelativeTimeShort(node.dateCreated)}
          </div>

        </div>

      </div>

    </div>
  );
};

// Dev Tools
VideoSummary.displayName = 'VideoSummary';

export default VideoSummary;
